import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { fileUrl, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import ProductCard, { formatPrice } from "@/components/ProductCard";
import StarRating from "@/components/StarRating";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useFavorites } from "@/context/FavoritesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, ShoppingCart, Download, ShieldCheck, Zap, Loader2, Sparkles, Tag, X, MessageSquare, Heart, Share2, Link2 } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const { isFav, toggle } = useFavorites();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [owned, setOwned] = useState(false);
  const [buying, setBuying] = useState(false);
  const [reviewData, setReviewData] = useState({ average: 0, count: 0, reviews: [] });
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
    api.get(`/products/${id}/reviews`).then((r) => setReviewData(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) {
      api.get("/orders/mine").then((r) => {
        setOwned(r.data.some((o) => o.product_id === id));
      }).catch(() => {});
    } else setOwned(false);
  }, [user, id]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode.trim(), product_id: id });
      setCoupon(data);
      toast.success(t("coupon.applied"));
    } catch (e) {
      setCoupon(null);
      toast.error(apiErr(e.response?.data?.detail) || t("coupon.invalid"));
    } finally { setApplyingCoupon(false); }
  };

  const removeCoupon = () => { setCoupon(null); setCouponCode(""); };

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggle(product.id);
    if (res?.needAuth) navigate("/login");
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("product.linkCopied")); }
    catch { toast.error("Error"); }
  };
  const shareLinks = [
    { key: "wa", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent((product?.title || "") + " " + shareUrl)}` },
    { key: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(product?.title || "")}&url=${encodeURIComponent(shareUrl)}` },
    { key: "fb", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
  ];

  const buy = async () => {
    if (!user) { navigate("/login"); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: id, origin_url: window.location.origin,
        coupon_code: coupon ? coupon.code : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setBuying(false);
    }
  };

  const submitReview = async () => {
    if (!myRating) { toast.error(t("review.yourRating")); return; }
    setSubmittingReview(true);
    try {
      await api.post(`/products/${id}/reviews`, { rating: myRating, comment: myComment });
      toast.success(t("review.thanks"));
      setMyRating(0); setMyComment("");
      const [rev, prod] = await Promise.all([
        api.get(`/products/${id}/reviews`), api.get(`/products/${id}`),
      ]);
      setReviewData(rev.data); setProduct(prod.data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setSubmittingReview(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/downloads/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = product.title || "download"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Download error");
    }
  };

  if (product === false) return <div className="max-w-7xl mx-auto px-5 py-32 text-center text-[#94A3B8]">Not found</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-5 py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0EA5E9]" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Left: gallery */}
        <div className="lg:col-span-7">
          {(() => {
            const gallery = (product.gallery && product.gallery.length ? product.gallery : [product.preview_image_url]).filter(Boolean);
            const current = gallery[activeImg] || product.preview_image_url;
            return (
              <>
                <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                  className="relative rounded-3xl overflow-hidden border border-[#E2E8F0] bg-white aspect-[4/3]">
                  {current ? (
                    <img src={fileUrl(current)} alt={product.title} className="h-full w-full object-cover" data-testid="gallery-main" />
                  ) : <div className="h-full w-full bg-gradient-to-br from-sky-100 to-amber-50" />}
                  <button onClick={onHeart} data-testid="detail-fav-btn" aria-label="favorite"
                    className="absolute top-4 right-4 h-11 w-11 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform">
                    <Heart className={`h-5 w-5 ${isFav(product.id) ? "fill-red-500 text-red-500" : "text-[#0F172A]"}`} />
                  </button>
                </motion.div>
                {gallery.length > 1 && (
                  <div className="flex gap-3 mt-4 overflow-x-auto pb-1" data-testid="gallery-thumbs">
                    {gallery.map((g, i) => (
                      <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                        className={`h-20 w-24 shrink-0 rounded-xl overflow-hidden border-2 transition-colors ${activeImg === i ? "border-[#0EA5E9]" : "border-[#E2E8F0] hover:border-[#0EA5E9]/40"}`}>
                        <img src={fileUrl(g)} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          <div className="mt-8">
            <h2 className="font-display text-xl font-extrabold text-[#0F172A] mb-4">{t("product.whatsIncluded")}</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {(product.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 bg-white border border-[#E2E8F0] rounded-xl p-4">
                  <span className="h-6 w-6 rounded-full bg-sky-50 text-[#0EA5E9] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm text-[#475569] leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right: buy panel */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            {product.category && (
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0EA5E9]">{product.category}</span>
            )}
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F172A] mt-2 leading-tight">{product.title}</h1>

            {product.rating_count > 0 && (
              <div className="mt-3"><StarRating value={product.rating_avg} count={product.rating_count} size={16} /></div>
            )}

            <Link to={`/seller/${product.seller_id}`} className="flex items-center gap-2 mt-4 group w-fit" data-testid="seller-link">
              {product.seller_avatar ? (
                <img src={fileUrl(product.seller_avatar)} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center">{(product.seller_name || "F")[0]}</div>
              )}
              <span className="text-sm text-[#475569] group-hover:text-[#0EA5E9]">{t("product.by")} <b>{product.seller_name}</b></span>
              {product.seller_verified && <VerifiedBadge size={15} />}
            </Link>

            <p className="text-[#475569] leading-relaxed mt-6 whitespace-pre-line">{product.description}</p>

            <div className="flex flex-wrap gap-2 mt-5">
              {(product.tags || []).map((tag) => (
                <span key={tag} className="text-xs bg-slate-100 text-[#475569] rounded-full px-3 py-1">#{tag}</span>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-5" data-testid="share-row">
              <span className="text-sm font-semibold text-[#475569] flex items-center gap-1.5"><Share2 className="h-4 w-4" /> {t("product.share")}:</span>
              <button onClick={copyLink} data-testid="share-copy"
                className="h-9 w-9 rounded-full border border-[#E2E8F0] flex items-center justify-center text-[#0F172A] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" title="Copy link">
                <Link2 className="h-4 w-4" />
              </button>
              {shareLinks.map((s) => (
                <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`share-${s.key}`}
                  className="h-9 px-3 rounded-full border border-[#E2E8F0] flex items-center justify-center text-xs font-bold text-[#475569] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors">
                  {s.label}
                </a>
              ))}
            </div>

            <div className="mt-8 bg-white border border-[#E2E8F0] rounded-2xl p-6">
              {coupon ? (
                <div className="mb-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-4xl font-extrabold text-[#0F172A]">{formatPrice(coupon.discounted_price, t("common.free"))}</span>
                    <span className="text-lg text-[#94A3B8] line-through">{formatPrice(coupon.original_price, t("common.free"))}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm text-emerald-600 font-semibold">
                    <Tag className="h-4 w-4" /> {coupon.code}
                    <button onClick={removeCoupon} className="text-[#94A3B8] hover:text-red-500" data-testid="coupon-remove"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ) : (
                <div className="font-display text-4xl font-extrabold text-[#0F172A] mb-4">{formatPrice(product.price, t("common.free"))}</div>
              )}

              {!owned && product.price > 0 && !coupon && (
                <div className="flex gap-2 mb-4">
                  <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder={t("coupon.placeholder")} data-testid="coupon-input"
                    className="rounded-xl h-10 text-sm" />
                  <Button onClick={applyCoupon} disabled={applyingCoupon} variant="outline" data-testid="coupon-apply"
                    className="rounded-xl h-10 shrink-0">
                    {applyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : t("coupon.apply")}
                  </Button>
                </div>
              )}

              {owned ? (
                <Button onClick={download} data-testid="download-btn"
                  className="w-full rounded-full h-12 bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold text-base">
                  <Download className="h-5 w-5 mr-2" /> {t("product.owned")}
                </Button>
              ) : (
                <Button onClick={buy} disabled={buying} data-testid="buy-btn"
                  className="w-full rounded-full h-12 bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold text-base">
                  {buying ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <ShoppingCart className="h-5 w-5 mr-2" />}
                  {t("product.buy")}
                </Button>
              )}
              <div className="mt-5 space-y-2.5">
                <div className="flex items-center gap-2 text-sm text-[#475569]"><Zap className="h-4 w-4 text-[#0EA5E9]" /> {t("product.instant")}</div>
                <div className="flex items-center gap-2 text-sm text-[#475569]"><ShieldCheck className="h-4 w-4 text-[#0EA5E9]" /> {t("product.secure")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-20 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-5 w-5 text-[#0EA5E9]" />
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A]">{t("review.title")}</h2>
          {reviewData.count > 0 && <StarRating value={reviewData.average} count={reviewData.count} size={18} />}
        </div>

        {owned && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6" data-testid="review-form">
            <h3 className="font-display font-bold text-[#0F172A] mb-3">{t("review.write")}</h3>
            <div className="mb-3">
              <span className="text-sm text-[#475569] block mb-1.5">{t("review.yourRating")}</span>
              <StarRating value={myRating} interactive size={26} onChange={setMyRating} />
            </div>
            <Textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} rows={3}
              placeholder={t("review.comment")} data-testid="review-comment" className="rounded-xl resize-none" />
            <Button onClick={submitReview} disabled={submittingReview} data-testid="review-submit"
              className="mt-3 rounded-full bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold">
              {submittingReview && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("review.submit")}
            </Button>
          </div>
        )}

        {reviewData.reviews.length === 0 ? (
          <p className="text-[#94A3B8]" data-testid="reviews-empty">{t("review.empty")}</p>
        ) : (
          <div className="space-y-4" data-testid="reviews-list">
            {reviewData.reviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center">{(r.buyer_name || "U")[0]}</div>
                    <span className="font-semibold text-[#0F172A] text-sm">{r.buyer_name}</span>
                  </div>
                  <StarRating value={r.rating} size={14} />
                </div>
                {r.comment && <p className="text-[#475569] text-sm mt-3 leading-relaxed">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {related.length > 0 && (
        <section className="mt-20">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-[#0EA5E9]" />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F172A]">{t("product.aiRelated")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
